import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppData } from "../context/AppDataContext";

function excerpt(text, maxLen = 100) {
  if (!text || typeof text !== "string") return "";
  const trimmed = text.replace(/\s+/g, " ").trim();
  return trimmed.length <= maxLen ? trimmed : trimmed.slice(0, maxLen).trim() + "…";
}

function scrollToTours() {
  const el = document.getElementById("tours");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const DEFAULT_DESTINATIONS = [
  {
    id: "madrid",
    image: "/images/madrid/1000107769.jpg",
    title: {
      hr: "Madrid",
      en: "Madrid",
      de: "Madrid",
      es: "Madrid",
    },
    text: {
      hr: "Grad energije, tapasa, rooftop terasa i bogate kulture.",
      en: "A city of energy, tapas, rooftop terraces and rich culture.",
      de: "Stadt voller Energie, Tapas, Rooftop-Terrassen und Kultur.",
      es: "Ciudad de energía, tapas, terrazas rooftop y mucha cultura.",
    },
    detail: {
      hr: "Madrid spaja povijest i moderan ritam života. Idealna je baza za city break, muzeje, gastronomiju i večernji izlazak.",
      en: "Madrid blends history with modern rhythm. Perfect for city breaks, museums, gastronomy and evening life.",
      de: "Madrid verbindet Geschichte und modernen Lebensrhythmus. Ideal für City-Breaks, Museen und Gastronomie.",
      es: "Madrid combina historia y ritmo moderno. Ideal para escapadas urbanas, museos y gastronomía.",
    },
  },
  {
    id: "valladolid",
    image: "/images/valladolid/valladolid.jpeg",
    title: {
      hr: "Valladolid",
      en: "Valladolid",
      de: "Valladolid",
      es: "Valladolid",
    },
    text: {
      hr: "Španjolski jezik, autentična kultura i vrhunska gastronomija.",
      en: "Spanish language, authentic culture and outstanding gastronomy.",
      de: "Spanischkurs, authentische Kultur und hervorragende Gastronomie.",
      es: "Idioma español, cultura auténtica y gastronomía excelente.",
    },
    detail: {
      hr: "Valladolid je odličan izbor za učenje španjolskog jezika i upoznavanje Castille kroz izlete u Segoviu i Salamancu.",
      en: "Valladolid is an excellent choice for learning Spanish and exploring Castile with Segovia and Salamanca excursions.",
      de: "Valladolid ist ideal zum Spanischlernen und zur Erkundung Kastiliens mit Ausflügen nach Segovia und Salamanca.",
      es: "Valladolid es ideal para aprender español y descubrir Castilla con excursiones a Segovia y Salamanca.",
    },
  },
  {
    id: "newyork",
    image: "/images/newyork/1000168199.jpg",
    title: {
      hr: "New York",
      en: "New York",
      de: "New York",
      es: "Nueva York",
    },
    text: {
      hr: "Metropola koja nikad ne spava - ritam, arhitektura i iskustva.",
      en: "The city that never sleeps - rhythm, architecture and experiences.",
      de: "Die Stadt, die niemals schläft - Rhythmus, Architektur und Erlebnisse.",
      es: "La ciudad que nunca duerme - ritmo, arquitectura y experiencias.",
    },
    detail: {
      hr: "New York nudi spoj ikoničnih lokacija, urbanog ritma i nezaboravnih pogleda - od Times Squarea do Brooklyna.",
      en: "New York offers iconic locations, urban energy and unforgettable views - from Times Square to Brooklyn.",
      de: "New York bietet ikonische Orte, urbanen Rhythmus und unvergessliche Ausblicke - vom Times Square bis Brooklyn.",
      es: "Nueva York ofrece lugares icónicos, energía urbana y vistas inolvidables, desde Times Square hasta Brooklyn.",
    },
  },
];

export default function Destinations() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { destinations, travelogues } = useAppData();
  const base = (i18n.language || "hr").slice(0, 2);
  const useCms = destinations && destinations.length > 0;
  const [selectedCard, setSelectedCard] = useState(null);
  const [showAllTravelogues, setShowAllTravelogues] = useState(false);

  const handleCardKeyDown = (e, cardId) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setSelectedCard(cardId);
    }
  };

  const closeModal = () => setSelectedCard(null);

  useEffect(() => {
    if (!selectedCard) return;
    const onEscape = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEscape);
      document.body.style.overflow = "";
    };
  }, [selectedCard]);

  const handleCtaFromModal = () => {
    closeModal();
    setTimeout(scrollToTours, 100);
  };

  const getModalContent = () => {
    if (useCms && typeof selectedCard === "number") {
      const dest = destinations.find((d) => d.id === selectedCard);
      if (!dest) return null;
      const body = dest.text?.[base] || dest.text?.hr;
      return {
        title: dest.title?.[base] || dest.title?.hr,
        text: null,
        detail: body,
      };
    }
    const fallbackDest = DEFAULT_DESTINATIONS.find((d) => d.id === selectedCard);
    if (fallbackDest) {
      return {
        title: fallbackDest.title[base] || fallbackDest.title.hr,
        text: fallbackDest.text[base] || fallbackDest.text.hr,
        detail: fallbackDest.detail[base] || fallbackDest.detail.hr,
      };
    }
    return null;
  };

  const modalContent = getModalContent();

  return (
    <section id="destinations" className="section section-alt">
      <div className="container">
        <header className="section-header">
          <h2>{t("destinations.sectionTitle")}</h2>
          <p>{t("destinations.sectionSubtitle")}</p>
        </header>
        <div className="destinations-grid">
          {useCms ? (
            destinations.map((dest) => (
              <article
                key={dest.id}
                className="destination-card destination-card--clickable"
                onClick={() => setSelectedCard(dest.id)}
                onKeyDown={(e) => handleCardKeyDown(e, dest.id)}
                role="button"
                tabIndex={0}
                aria-label={dest.title?.[base] || dest.title?.hr}
              >
                <div
                  className={`destination-image ${dest.image ? "" : dest.image_class || "dest-1"}`}
                  style={dest.image ? { backgroundImage: `url(${dest.image})` } : undefined}
                />
                <div className="destination-body">
                  <h3>{dest.title?.[base] || dest.title?.hr}</h3>
                  <p>{dest.text?.[base] || dest.text?.hr}</p>
                  <span className="destination-cta">{t("destinations.cta")} →</span>
                </div>
              </article>
            ))
          ) : (
            <>
              {DEFAULT_DESTINATIONS.map((dest) => (
                <article
                  key={dest.id}
                  className="destination-card destination-card--clickable"
                  onClick={() => setSelectedCard(dest.id)}
                  onKeyDown={(e) => handleCardKeyDown(e, dest.id)}
                  role="button"
                  tabIndex={0}
                  aria-label={dest.title[base] || dest.title.hr}
                >
                  <div className="destination-image" style={{ backgroundImage: `url(${dest.image})` }} />
                  <div className="destination-body">
                    <h3>{dest.title[base] || dest.title.hr}</h3>
                    <p>{dest.text[base] || dest.text.hr}</p>
                    <span className="destination-cta">{t("destinations.cta")} →</span>
                  </div>
                </article>
              ))}
            </>
          )}
        </div>

        {travelogues && travelogues.length > 0 && (
          <>
            <header className="section-header destinations-travelogue-header">
              <h2>{t("destinations.travelogueCardsTitle") || "Putopisi"}</h2>
            </header>
            <div className="destinations-grid destinations-grid--travelogues">
              {(showAllTravelogues ? travelogues : travelogues.slice(0, 3)).map((travelogue) => {
                const title = travelogue.title?.[base] || travelogue.title?.en;
                const content = travelogue.content?.[base] || travelogue.content?.en || "";
                return (
                  <article
                    key={travelogue.id}
                    className="destination-card destination-card--clickable destination-card--travelogue"
                    onClick={() => navigate(`/travelogue/${travelogue.key}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/travelogue/${travelogue.key}`);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={title}
                  >
                    <div
                      className="destination-image destination-image--travelogue"
                      style={{ backgroundImage: travelogue.image ? `url(${travelogue.image})` : undefined }}
                    />
                    <div className="destination-body">
                      <h3>{title}</h3>
                      <p>{excerpt(content)}</p>
                      <span className="destination-cta">
                        {t("destinations.travelogueCta") || "Pročitaj putopis"} →
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
            {travelogues.length > 3 && (
              <div className="destinations-travelogue-actions">
                <button
                  type="button"
                  className="btn ghost destinations-travelogue-show-more"
                  onClick={() => setShowAllTravelogues((v) => !v)}
                >
                  {showAllTravelogues
                    ? t("destinations.travelogueShowLess") || "Prikaži manje"
                    : t("destinations.travelogueShowMore") || "Prikaži više"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedCard && modalContent && (
        <div
          className="destination-modal-overlay"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="destination-modal-title"
        >
          <div
            className="destination-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="destination-modal-close"
              onClick={closeModal}
              aria-label={t("tourPopup.close") || "Zatvori"}
            >
              ×
            </button>
            <h2 id="destination-modal-title" className="destination-modal-title">
              {modalContent.title}
            </h2>
            {modalContent.text && (
              <p className="destination-modal-teaser">{modalContent.text}</p>
            )}
            <p className="destination-modal-detail">{modalContent.detail}</p>
            <div className="destination-modal-actions">
              <button type="button" className="btn primary" onClick={handleCtaFromModal}>
                {t("destinations.cta")} →
              </button>
              <button type="button" className="btn ghost" onClick={closeModal}>
                {t("tourPopup.close") || "Zatvori"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
